import React from "react";
import styled from "styled-components";
import { Button, Col, Row } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";
import { get, isEmpty, uniqueId } from "lodash-es";
import { FieldArray, Form, useFormikContext } from "formik";

// Functions
import { trackingFormModel } from "../../../helpers/tracking/formModel";
import { joinClasses } from "../../../helpers/utils";
import { showToast } from "../../../helpers/toastEmitters";

// Components
import TrackingFieldRemovable from "../TrackingFieldRemovable";
import TrackingFieldWithButton from "./TrackingFieldWithButton";
import SubmitBtn from "../../form/SubmitBtn";

const FormContainer = styled(Form)`
  .${trackingFormModel.formFields.trackingNumbers.name} {
    h3 {
      font-family: Open Sans;
      font-style: normal;
      font-weight: bold;
      font-size: 18px;
      line-height: 24px;
      letter-spacing: 0.1px;
      color: #ffffff;
    }
    button {
      width: 7.438rem;
    }
  }
`;

const TrackingInstructions = styled(Col)`
  background-color: transparent;
  font-family: Open Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.2px;
  color: #ffffff;
  margin-bottom: 14px;
`;

const AddTextBoxButton = styled(Button)`
  font-family: Open Sans;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 0.15px;
  color: #ffffff;
  span {
    font-size: 32px;
    font-weight: bolder;
  }

  @media screen and (max-width: 767px) {
    font-size: 15px;
    padding: 1rem;
    span {
      margin-right: 0.5rem;
      font-weight: 500;
    }
  }
`;

const TrackingButtons = styled(Row)`
  flex-direction: column;
  @media screen and (min-width: 576px) {
    flex-direction: row;
  }
`;

const NewTrackingForm = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { isSubmitting } = useFormikContext();

  return (
    <>
      <FormContainer id={trackingFormModel.formId}>
        <FieldArray name={trackingFormModel.formFields.trackingNumbers.name}>
          {({
            replace,
            remove,
            push,
            form: { values, handleBlur, errors },
          }) => {
            const onPaste = ({ clipboardData }, idx) => {
              const pastedTrackingNumbers = [
                ...clipboardData
                  .getData("Text")
                  .replaceAll(",", "\n")
                  .split(/\n/),
              ];

              const currentTrackingNosCount = values.trackingNumbers.length;
              const pastedTrackingNosCount = pastedTrackingNumbers.length;

              if (currentTrackingNosCount > 25) {
                return;
              }

              if (pastedTrackingNosCount > 0) {
                // Get number of items pasted allowed to be added
                const currentAndPastedNum =
                  currentTrackingNosCount - 1 + pastedTrackingNosCount;
                let numAirbillsToAdd = pastedTrackingNosCount;

                if (currentAndPastedNum > 25) {
                  showToast(
                    "info",
                    "You've reached the limit for airbills to track."
                  );
                  numAirbillsToAdd = Math.abs(26 - currentTrackingNosCount);
                }

                const airbillsToAdd = pastedTrackingNumbers.slice(
                  0,
                  numAirbillsToAdd
                );

                const valueFirstAirbill = get(
                  airbillsToAdd,
                  "[0]",
                  ""
                ).replaceAll(/\r?\n|\r/g, "");

                replace(idx, {
                  ...values.trackingNumbers[idx],
                  value: valueFirstAirbill,
                });

                if (numAirbillsToAdd > 1) {
                  let currentIndex = idx;

                  airbillsToAdd
                    .slice(1, numAirbillsToAdd)
                    .forEach((pastedValue = "", index) => {
                      currentIndex++;

                      if (values.trackingNumbers[currentIndex]) {
                        if (
                          !isEmpty(values.trackingNumbers[currentIndex].value)
                        ) {
                          replace(currentIndex, {
                            ...values.trackingNumbers[currentIndex],
                            value: pastedValue.replaceAll(/\r?\n|\r/g, ""),
                          });
                        }
                      }

                      push({
                        name: getTrackingNewId(),
                        value: pastedValue.replaceAll(/\r?\n|\r/g, ""),
                      });
                    });
                }
              }
              clipboardData.clearData();
            };

            const showTrackingNumberField = {
              single: values.trackingNumbers.length < 2,
              multiple: values.trackingNumbers.length > 1,
            };

            const getTrackingNewId = () => {
              const newTrackingId = `TRACK${uniqueId("REF")}`;

              if (
                values.trackingNumbers
                  .map(({ name }) => name)
                  .includes(newTrackingId)
              ) {
                return getTrackingNewId();
              }

              return newTrackingId;
            };

            const handleOnchange = (idx, value) => {
              replace(idx, value);
            };

            const onPasteCapture = (e) => {
              e.preventDefault();
            };

            return (
              <>
                {showTrackingNumberField.single &&
                  values.trackingNumbers.map(({ name, value }, idx) => {
                    const error = get(
                      errors,
                      `${trackingFormModel.formFields.trackingNumbers.name}[${idx}].value`,
                      ""
                    );

                    return (
                      <Row key={`tracking-number-${idx}`}>
                        <Col>
                          <TrackingFieldWithButton
                            id={getTrackingNewId()}
                            label={
                              trackingFormModel.formFields.trackingNumbers.label
                            }
                            placeholder={
                              trackingFormModel.formFields.trackingNumbers
                                .placeholder
                            }
                            onChange={(e) => {
                              handleOnchange(idx, {
                                name,
                                value: e.target.value,
                              });
                            }}
                            onBlur={handleBlur}
                            size="lg"
                            formGroupClasses={joinClasses([
                              "mb-48px",
                              trackingFormModel.formFields.trackingNumbers.name,
                            ])}
                            // onButtonClick={handleSubmit}
                            value={value}
                            error={error}
                            onPaste={(e) => onPaste(e, idx)}
                            onPasteCapture={onPasteCapture}
                            labelClassname="text-white"
                            isTracking={isSubmitting}
                            classes="border-0"
                          />
                        </Col>
                      </Row>
                    );
                  })}
                {showTrackingNumberField.multiple && (
                  <Row className="mb-48px">
                    <TrackingInstructions>
                      Add text boxes to track multiple packages at once
                    </TrackingInstructions>
                    <Col md={12}>
                      {values.trackingNumbers.map(
                        ({ name, value, touched }, idx) => {
                          const error = get(
                            errors,
                            `${trackingFormModel.formFields.trackingNumbers.name}[${idx}].value`,
                            ""
                          );
                          return (
                            <Row key={`${name}-${idx}`}>
                              <Col md={12}>
                                <TrackingFieldRemovable
                                  id={getTrackingNewId()}
                                  placeholder="Enter tracking number..."
                                  size="lg"
                                  formGroupClasses={`mb-4 track-more ${trackingFormModel.formFields.trackingNumbers.name}`}
                                  fieldsLength={values.trackingNumbers.length}
                                  onRemove={() => {
                                    remove(idx);
                                  }}
                                  onPasteCapture={onPasteCapture}
                                  onPaste={(e) => onPaste(e, idx)}
                                  onBlur={handleBlur}
                                  value={value}
                                  onChange={(e) => {
                                    handleOnchange(idx, {
                                      name,
                                      value: e.target.value,
                                    });
                                  }}
                                  error={error}
                                  touched={touched}
                                  noLabel
                                />
                              </Col>
                            </Row>
                          );
                        }
                      )}
                    </Col>
                  </Row>
                )}

                <TrackingButtons>
                  <Col
                    sm={showTrackingNumberField.single ? 12 : 8}
                    className={
                      showTrackingNumberField.multiple && "mb-18px mb-md-0"
                    }
                  >
                    <AddTextBoxButton
                      variant="secondary"
                      className={joinClasses([
                        showTrackingNumberField.single ? "ml-auto" : "",
                        isMobile && "btn-block",
                      ])}
                      onClick={() =>
                        push({
                          name: getTrackingNewId(),
                          value: "",
                        })
                      }
                      disabled={values.trackingNumbers.length >= 25}
                    >
                      <span className="mr-3">+</span> Add more tracking numbers
                    </AddTextBoxButton>
                  </Col>
                  {showTrackingNumberField.multiple && (
                    <Col sm={4} xs={12}>
                      <SubmitBtn
                        isSubmitting={isSubmitting}
                        className={joinClasses([
                          isMobile && "btn-block",
                          "ml-md-auto",
                        ])}
                        // onClick={handleSubmit}
                      >
                        Track
                      </SubmitBtn>
                    </Col>
                  )}
                </TrackingButtons>
              </>
            );
          }}
        </FieldArray>
      </FormContainer>
    </>
  );
};

export default NewTrackingForm;
